import { useRef } from "react";
import { type SessionCardSource, trackSessionCardDownloaded } from "../lib/sessionEvents";

/**
 * The two ways a goon card leaves the page: the OS share sheet where there is one, and
 * a plain download everywhere. One component, because the card is drawn in two places
 * now — the recap a stopped session pops on the screen, and the profile's history —
 * and the share sheet's quirks are the kind of thing that drifts when copied.
 *
 * `canShare({files})` is checked rather than `share` alone: Web Share exists in browsers
 * that refuse files, and calling it with an unsupported payload throws. Any failure
 * (including the user dismissing the sheet) falls through to the download, which is
 * always rendered anyway. On iOS Safari an `<a download>` drops the file into Downloads
 * where the user then has to go find it — which is why the share button leads there.
 */
export function SessionCardDownload({
  url,
  source,
  filename = "goon-session.png",
}: {
  /** The rendered card as a data URL, or null while it is still drawing. */
  url: string | null;
  /** Which surface drew it — the recap or the profile's history. Analytics only. */
  source: SessionCardSource;
  filename?: string;
}) {
  // Once per card shown, whichever door: a share that falls through to the download
  // is one act, not two.
  const counted = useRef(false);
  const count = () => {
    if (counted.current) return;
    counted.current = true;
    trackSessionCardDownloaded(source);
  };

  async function shareNative() {
    if (!url) return;
    count();
    try {
      const blob = await (await fetch(url)).blob();
      const file = new File([blob], filename, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file] });
      }
    } catch {
      /* dismissed, or unsupported — the Download link beside this is the fallback */
    }
  }

  const canShareFiles =
    typeof navigator !== "undefined" && typeof navigator.canShare === "function";

  return (
    <>
      {canShareFiles && (
        <button
          type="button"
          onClick={shareNative}
          disabled={!url}
          /* text-black, not text-white: white on #f5c518 is about 1.8:1, which fails
             every contrast bar there is. The gold is the loud part. */
          className="flex-1 rounded-xl bg-[var(--color-accent)] px-4 py-2.5 text-sm font-semibold text-black hover:opacity-90 disabled:opacity-50"
        >
          Share ↗
        </button>
      )}
      <a
        href={url ?? "#"}
        download={filename}
        onClick={count}
        className={`flex-1 rounded-xl px-4 py-2.5 text-center text-sm font-semibold ${
          canShareFiles
            ? "border border-[var(--color-edge)] text-zinc-200 hover:bg-[var(--color-panel-2)]"
            : "bg-[var(--color-accent)] text-black hover:opacity-90"
        } ${!url ? "pointer-events-none opacity-50" : ""}`}
      >
        Download
      </a>
    </>
  );
}
